import {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography} from "@mui/material";
import Grid from "@mui/material/Grid";
import MyBooleanMark from "../../components/MyBooleanMark";
import {toPercentage} from "../../util/NumberUtil";
import {DATA_TYPES, EVENT_FIELDS, VIEW_TYPES} from "../../constants";
import MyTextField from "../../components/MyTextField";
import MySelect from "../../components/MySelect";
import MyMultiSelectCheckboxes from "../../components/MyMultiSelectCheckboxes";
import MyCheckbox from "../../components/MyCheckbox";
const getInitialFormData = () => {
    let initialFormData = {};

    EVENT_FIELDS.forEach((field) => {
        if (field?.key) {
            initialFormData[field.key] = {...initialFormData[field.key], [field.name]: {}};
        } else {
            initialFormData[field.name] = {};
        }

        switch (field.viewType) {
            case VIEW_TYPES.TEXT:
                if (field?.key) {
                    initialFormData[field.key][field.name].value = "";
                } else {
                    initialFormData[field.name].value = "";
                }
                break;
            case VIEW_TYPES.SINGLE_SELECT:
                if (field?.key) {
                    initialFormData[field.key][field.name].value = "";
                    initialFormData[field.key][field.name].options = [];
                } else {
                    initialFormData[field.name].value = "";
                    initialFormData[field.name].options = [];
                }

                break;
            case VIEW_TYPES.MULTI_SELECT:
                if (field?.key) {
                    initialFormData[field.key][field.name].value = [];
                    initialFormData[field.key][field.name].options = [];
                } else {
                    initialFormData[field.name].value = [];
                    initialFormData[field.name].options = [];
                }

                break;
            case VIEW_TYPES.CHECKBOX:
                if (field?.key) {
                    initialFormData[field.key][field.name].value = field.defaultValue;
                } else {
                    initialFormData[field.name].value = field.defaultValue;
                }
                break;
        }
    });

    return initialFormData;
};

const EventModal = forwardRef(({eventDetail}, ref) => {
    const [open, setOpen] = useState(false);
    useImperativeHandle(
        ref,
        () => ({
            handleOpen,
        }),
        []
    );
    const [formData, setFormData] = useState(getInitialFormData);
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };
    const handleInputChange = () => {};
    const isRepFieldEditable = (field) => {
        return field.selfEditable;
    };
    const updateFormData = (eventDetail) => {
        setFormData((prev) => {
            let newFormData = {...prev};
            EVENT_FIELDS.forEach((field) => {
                if (field?.key) {
                    if (eventDetail[field.key]) {
                        switch (field.dataType) {
                            case DATA_TYPES.STRING:
                            case DATA_TYPES.TIMESTAMP:
                                newFormData[field.key][field.name] = {
                                    ...prev[field.key][field.name],
                                    value: eventDetail[field.key][field.name] ?? "",
                                };
                                break;
                            case DATA_TYPES.BOOLEAN:
                                newFormData[field.key][field.name] = {
                                    ...prev[field.key][field.name],
                                    value: !!eventDetail[field.key][field.name],
                                };
                                break;
                            case DATA_TYPES.STRING_ARRAY:
                                newFormData[field.key][field.name] = {
                                    ...prev[field.key][field.name],
                                    value: eventDetail[field.key][field.name] ?? [],
                                };
                                break;
                            case DATA_TYPES.OBJECT:
                                newFormData[field.key][field.name] = {
                                    ...prev[field.key][field.name],
                                    value: eventDetail[field.key][field.name]?.id ?? "",
                                };
                                break;
                        }
                    }
                } else {
                    switch (field.dataType) {
                        case DATA_TYPES.STRING:
                        case DATA_TYPES.TIMESTAMP:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: eventDetail[field.name] ?? "",
                            };
                            break;
                        case DATA_TYPES.BOOLEAN:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: !!eventDetail[field.name],
                            };
                            break;
                        case DATA_TYPES.STRING_ARRAY:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: eventDetail[field.name] ?? [],
                            };
                            break;
                        case DATA_TYPES.OBJECT:
                            newFormData[field.name] = {
                                ...prev[field.name],
                                value: eventDetail[field.name]?.id ?? "",
                            };
                            break;
                    }
                }
            });
            return newFormData;
        });
    };
    useEffect(() => {
        if (eventDetail && open) {
            updateFormData(eventDetail);
        }
    }, [eventDetail, open]);
    const fieldsDefault = EVENT_FIELDS.filter((field) => !field?.key);
    const fieldsShipto = EVENT_FIELDS.filter((field) => field?.key == "shipto");
    const fieldsLocation = EVENT_FIELDS.filter((field) => field?.key == "location");
    const fieldsEventType = EVENT_FIELDS.filter((field) => field?.key == "eventType");
    const fieldsEventProductType = EVENT_FIELDS.filter((field) => field?.key == "eventProductType");
    if (!eventDetail) {
        return <></>;
    }
    const renderFields = (fields) => {
        return fields.map((field, index) => {
            let value = "";
            let options = [];
            if (field?.key) {
                value = formData[field.key][field.name]?.value;
                options = formData[field.key][field.name]?.options;
            } else {
                value = formData[field.name]?.value;
                options = formData[field.name]?.options;
            }
            switch (field.viewType) {
                case VIEW_TYPES.TEXT:
                    if (field?.key) {
                        value = formData[field.key][field.name].value;
                    } else {
                        value = formData[field.name].value;
                    }
                    if (field.dataType === DATA_TYPES.TIMESTAMP) {
                        value = value ? new Date(value).toLocaleString() : "";
                    }
                    return (
                        <Grid item xs={12} key={field.index}>
                            <MyTextField
                                value={value}
                                editable={isRepFieldEditable(field)}
                                label={field.label}
                                type="text"
                            />
                        </Grid>
                    );
                case VIEW_TYPES.SINGLE_SELECT:
                    if (field?.key) {
                        options = formData[field.key][field.name].options;
                        if (options.length === 0) {
                            if (field.dataType === DATA_TYPES.OBJECT) {
                                options = [
                                    {
                                        id: formData[field.key][field.name]?.id ?? "",
                                        name: formData[field.key][field.name]?.name ?? "",
                                    },
                                ];
                            } else {
                                options = [
                                    {
                                        id: formData[field.key][field.name] ?? "",
                                        name: formData[field.key][field.name] ?? "",
                                    },
                                ];
                            }
                        }
                    } else {
                        options = formData[field.name].options;
                        if (options.length === 0) {
                            if (field.dataType === DATA_TYPES.OBJECT) {
                                options = [
                                    {
                                        id: formData[field.name]?.id ?? "",
                                        name: formData[field.name]?.name ?? "",
                                    },
                                ];
                            } else {
                                options = [
                                    {
                                        id: formData[field.name] ?? "",
                                        name: formData[field.name] ?? "",
                                    },
                                ];
                            }
                        }
                    }

                    return (
                        <Grid item xs={12} key={index}>
                            <MySelect
                                key={resetKey}
                                label={field.label}
                                options={options}
                                value={value}
                                editable={isRepFieldEditable(field)}
                                required={mode === "create" && field.requiredForCreate}
                                autoSelect={value}
                                onChange={(value) => handleInputChange(field.name, value)}
                            />
                        </Grid>
                    );
                case VIEW_TYPES.MULTI_SELECT:
                    return (
                        <Grid item xs={12} key={field.name}>
                            <MyMultiSelectCheckboxes
                                title={field.label}
                                options={options}
                                value={value}
                                editable={isRepFieldEditable(field)}
                                onChange={(value) => handleInputChange(field.name, value)}
                            />
                        </Grid>
                    );
                case VIEW_TYPES.CHECKBOX:
                    return (
                        <Grid item xs={12} key={field.name}>
                            <MyCheckbox
                                checked={!!value}
                                editable={isRepFieldEditable(field)}
                                label={field.label}
                                onChange={(event) => handleInputChange(field.name, event.target.checked)}
                            />
                        </Grid>
                    );
            }
        });
    };
    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" key={eventDetail?.id}>
                <DialogTitle>
                    {`EventId: #${eventDetail?.id}`}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1}>
                        {renderFields(fieldsEventType)}
                        {renderFields(fieldsEventProductType)}
                        {renderFields(fieldsDefault)}
                        <Grid item>
                            <Typography variant="h6">Location</Typography>
                        </Grid>
                        {renderFields(fieldsLocation)}
                        <Grid item>
                            <Typography variant="h6">Shipto</Typography>
                        </Grid>
                        {renderFields(fieldsShipto)}
                        <Grid item xs={12}>
                            <Typography variant="h6">Fittings Info</Typography>
                            <Typography>
                                <strong>Fitting Duration: </strong>
                                {eventDetail?.fittingDurationOpt?.name ?? ""}
                            </Typography>
                            <Box display="flex" alignItems="center">
                                <Box>
                                    <strong>Fitting Count: </strong>
                                    {eventDetail?.fittingsCount ?? "0"}
                                </Box>
                                {eventDetail?.fittingsCount > 0 && (
                                    <Box paddingLeft={"5px"}>
                                        (
                                        <Link
                                            href={`/fittings?eventId=${eventDetail?.id}`}
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            View Fittings
                                        </Link>
                                        )
                                    </Box>
                                )}
                            </Box>
                            <Typography>
                                <strong>Booked Percentage: </strong>
                                {toPercentage(eventDetail?.bookedPercentage)}
                            </Typography>
                            <Typography>
                                <strong>Completed Percentage: </strong>
                                {toPercentage(eventDetail?.completedPercentage)}
                            </Typography>
                        </Grid>
                    </Grid>
                    {/* <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography>
                                <strong>Scheduled Start: </strong>
                                {eventDetail?.scheduledStart
                                    ? new Date(eventDetail?.scheduledStart).toLocaleString()
                                    : "Unknown"}
                            </Typography>
                            <Typography>
                                <strong>Scheduled End: </strong>
                                {eventDetail?.scheduledEnd
                                    ? new Date(eventDetail?.scheduledEnd).toLocaleString()
                                    : "Unknown"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6">Location</Typography>
                            {eventDetail?.location ? (
                                <>
                                    <Typography>{eventDetail?.location?.name}</Typography>
                                    <Typography>
                                        {eventDetail?.location?.address1}, {eventDetail?.location?.address2}
                                    </Typography>
                                    <Typography>
                                        {eventDetail?.location?.city}, {eventDetail?.location?.state}{" "}
                                        {eventDetail?.location?.postalCode}
                                    </Typography>
                                </>
                            ) : (
                                "Unknown"
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6">ShipTo</Typography>
                            {eventDetail?.shipto ? (
                                <>
                                    <Typography>{eventDetail?.shipto?.name}</Typography>
                                    <Typography>
                                        {eventDetail?.shipto?.address1}, {eventDetail?.shipto?.address2}
                                    </Typography>
                                    <Typography>
                                        {eventDetail?.shipto?.city}, {eventDetail?.shipto?.state ?? ""}{" "}
                                        {eventDetail?.shipto?.postalCode ?? ""}
                                    </Typography>
                                </>
                            ) : (
                                "Unknown"
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6">Golf Pro</Typography>
                            <Typography>
                                {eventDetail?.golfProFirstName ?? ""} {eventDetail?.golfProLastName ?? ""}
                            </Typography>
                            <Typography>Email: {eventDetail?.golfProEmail ?? "Unknown"}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6">Additional Info</Typography>
                            <Typography>
                                <strong>Fitter Count: </strong> {eventDetail?.fitterCount}
                            </Typography>
                            <Grid item display={"flex"}>
                                <Typography>
                                    <strong>Club Building: </strong>
                                </Typography>
                                <MyBooleanMark value={eventDetail?.onsiteClubBuilding} />
                            </Grid>
                            <Grid item display={"flex"}>
                                <Typography>
                                    <strong>Build Station: </strong>
                                </Typography>
                                {eventDetail?.onsiteBuildStation?.name ?? ""}
                            </Grid>
                            <Typography>
                                <strong>Share Link: </strong>
                                <Link href={eventDetail?.shareLink} target="_blank" rel="noopener">
                                    {eventDetail?.shareLink}
                                </Link>
                            </Typography>
                            <Typography>
                                <strong>Business Unit: </strong>
                                {eventDetail?.businessUnitCode}
                            </Typography>
                            <Grid item display={"flex"}>
                                <Typography>
                                    <strong>Active: </strong>
                                </Typography>
                                <MyBooleanMark value={eventDetail?.active} />
                            </Grid>
                            <Typography>
                                <strong>Language: </strong>
                                {eventDetail?.languageCode ?? ""}
                            </Typography>
                            <Typography>
                                <strong>Currency Code: </strong>
                                {eventDetail?.currencyCode ?? ""}
                            </Typography>
                            <Typography>
                                <strong>BookAbility Level: </strong>
                                {eventDetail?.bookabilityLevel?.bookabilityLevelName ?? "Unknown"}
                            </Typography>
                            <Typography>
                                <strong>Specs Visibility: </strong>
                                {eventDetail?.specsVisibility?.name ?? ""}
                            </Typography>
                        </Grid>
                    </Grid> */}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});
export default EventModal;
